import React from 'react';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';

// ============================================================================
// Terms of Use — boilerplate adapted for KBT (Kehilat Bnei Torah), Los
// Angeles. Reviewed against the existing Privacy Policy doc on file so
// names, address, and contact info line up. Replace the version date and
// any specific clauses as your counsel advises.
// ============================================================================

const SECTIONS = [
  {
    head: 'Acceptance of Terms',
    body: [
      'By accessing or using the Kehilat Bnei Torah website at https://www.kbtla.org (the "Website") and any related online services, you ("you" or "user") agree to be bound by these Terms of Use. If you do not agree, please do not use the Website.',
      'These Terms apply to all visitors, members, donors, and event registrants. We may update them from time to time; the most current version is always posted on this page.',
    ],
  },
  {
    head: 'Use of the Website',
    body: [
      'You agree to use the Website only for lawful purposes and in a manner that does not infringe the rights of, or restrict the use of, any third party.',
    ],
    list: [
      'Do not interfere with or disrupt the Website, its servers, or networks.',
      'Do not attempt to gain unauthorized access to any portion of the Website.',
      'Do not upload viruses, malicious code, or content that is unlawful, defamatory, or otherwise objectionable.',
      'Do not use the Website to harvest information about other users.',
    ],
  },
  {
    head: 'Membership, Donations, and Tickets',
    body: [
      'Memberships, donations, and event-ticket purchases made through the Website are subject to the terms shown at checkout. Donations to KBT are non-refundable except where required by law. Event tickets may be subject to additional event-specific terms (such as refund windows or transfer rules), which will be presented at the time of purchase.',
      'Tax-deductible receipts are issued to the email address on file. If you need a corrected receipt, contact us at office@kbtla.org.',
    ],
  },
  {
    head: 'User Content',
    body: [
      'If you submit content to the Website — such as comments, prayer requests, RSVPs, or any communications — you grant KBT a non-exclusive, royalty-free, worldwide license to use, reproduce, and display that content in connection with our religious, educational, and charitable activities.',
      'You represent that any content you submit is yours to share and does not violate the rights of any third party.',
    ],
  },
  {
    head: 'Intellectual Property',
    body: [
      'All text, graphics, logos, photographs, videos, audio (including divrei Torah recordings and shiurim), and software on the Website are the property of Kehilat Bnei Torah or our licensors, and are protected by United States and international copyright laws.',
      'You may view and download content from the Website for personal, non-commercial use. Any other use, including reproduction, distribution, or modification, requires our prior written consent.',
    ],
  },
  {
    head: 'Third-Party Links and Services',
    body: [
      'The Website may contain links to third-party websites or use third-party services (e.g., donation processors, calendar tools, video platforms). KBT is not responsible for the content, privacy practices, or terms of those third parties. Your use of them is at your own risk and subject to their own policies.',
    ],
  },
  {
    head: 'Disclaimer of Warranties',
    body: [
      'The Website is provided on an "as is" and "as available" basis, without warranties of any kind, express or implied. KBT does not warrant that the Website will be uninterrupted, error-free, or free of viruses or other harmful components.',
      'Times posted on the Website (zmanim, candle-lighting, prayer and shiur times) are provided for the convenience of the community and may be revised. Always confirm with the kehilla office or a current bulletin before relying on a specific time.',
    ],
  },
  {
    head: 'Limitation of Liability',
    body: [
      'To the maximum extent permitted by law, Kehilat Bnei Torah, its officers, directors, employees, volunteers, and agents will not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Website.',
    ],
  },
  {
    head: 'Indemnification',
    body: [
      'You agree to indemnify and hold KBT harmless from any claim, demand, loss, or expense (including reasonable attorneys\' fees) arising out of your use of the Website, your violation of these Terms, or your violation of the rights of any third party.',
    ],
  },
  {
    head: 'Governing Law',
    body: [
      'These Terms are governed by the laws of the State of California, without regard to its conflict-of-laws provisions. Any dispute arising out of or related to these Terms or the Website shall be resolved in the state or federal courts located in Los Angeles County, California, and you consent to the personal jurisdiction of those courts.',
    ],
  },
  {
    head: 'Changes to these Terms',
    body: [
      'We may revise these Terms from time to time. The revised version will be posted on this page with an updated date. Your continued use of the Website after a change indicates your acceptance of the updated Terms.',
    ],
  },
  {
    head: 'Contact Us',
    body: ['Questions about these Terms? Please contact us:'],
    contact: [
      { label: 'Email',   value: 'office@kbtla.org' },
      { label: 'Address', value: 'Kehilat Bnei Torah · 9017 W. Pico Blvd., Los Angeles, CA 90035' },
    ],
  },
];

function TermBlock({ section, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay: index * 0.03, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        sx={{
          pt: 5,
          pb: 5,
          borderTop: '1px solid rgba(36,34,88,0.10)',
        }}
      >
        <Box
          sx={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: { xs: 22, md: 28 },
            color: 'var(--navy-500)',
            letterSpacing: '-0.01em',
            mb: 2,
          }}
        >
          {section.head}
        </Box>

        <Stack spacing={2}>
          {section.body?.map((p, i) => (
            <Box
              key={i}
              sx={{
                fontFamily: 'var(--font-body)',
                fontSize: { xs: 15.5, md: 16.5 },
                lineHeight: 1.7,
                color: 'var(--text)',
                opacity: 0.92,
              }}
            >
              {p}
            </Box>
          ))}

          {section.list && (
            <Stack spacing={1.5} component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
              {section.list.map((item, i) => (
                <Stack
                  key={i}
                  component="li"
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      mt: 1,
                      flex: '0 0 auto',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: 'var(--teal-500)',
                    }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'var(--font-body)',
                      fontSize: { xs: 15.5, md: 16.5 },
                      lineHeight: 1.65,
                      color: 'var(--text)',
                      opacity: 0.92,
                    }}
                  >
                    {item}
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}

          {section.contact && (
            <Box
              sx={{
                mt: 2,
                p: 3,
                background: 'var(--cream-50)',
                border: '1px solid rgba(36,34,88,0.10)',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' },
                rowGap: 1.5,
                columnGap: 3,
              }}
            >
              {section.contact.map((row) => (
                <React.Fragment key={row.label}>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10.5,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {row.label}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 600,
                      fontSize: 16,
                      color: 'var(--navy-500)',
                    }}
                  >
                    {row.value}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </Stack>
      </Box>
    </motion.div>
  );
}

export default function Terms() {
  return (
    <EditorialPageShell
      title="Terms of Use"
      description="Terms of Use for the Kehilat Bnei Torah website."
    >
      <PageHero
        kicker="Legal  ·  KBT"
        meta="Updated"
        title="Terms"
        titleAccent="of use."
        subtitle="The rules of the road for kbtla.org — what you can expect from us, and what we ask of you in return."
        image="/assets/images/kbt/hero-sy.jpg"
        height="60vh"
      />

      <EditorialSection
        kicker="The Terms"
        title="Plainly stated,"
        titleAccent="and reviewed."
        align="left"
      >
        <Box sx={{ maxWidth: 820, mx: 'auto' }}>
          {SECTIONS.map((s, i) => (
            <TermBlock key={s.head} section={s} index={i} />
          ))}

          <Box
            sx={{
              mt: 6,
              pt: 4,
              borderTop: '1px solid rgba(36,34,88,0.10)',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            Last updated  ·  {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Box>
        </Box>
      </EditorialSection>
    </EditorialPageShell>
  );
}
